// ABOUTME: Workspace detail page with tabs for overview, usage, and config.
// ABOUTME: Shows workspace info, stats, and provides access to agent configuration.

import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Users,
  Database,
  FileText,
  Search,
  BarChart3,
  Settings,
  Edit,
  Save,
  X,
  Trash2,
  Plus,
  AlertTriangle,
  Key,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { workspaceApi, dataSourceApi } from '@/services/api/adminClient';
import type {
  WorkspaceStats,
  SubscriptionTier,
  SubscriptionStatus,
  DataSource,
  ServicePrincipal,
  WorkspaceUsageSummary,
} from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { UsageAnalytics } from './UsageAnalytics';
import { AgentConfigPanel } from './AgentConfigPanel';
import { DataSourceModal } from './DataSourceModal';
import { WorkspaceUsersTab } from './WorkspaceUsersTab';
import { UsageGaugeCard } from '@/components/admin/UsageGaugeCard';

interface WorkspaceDetailData {
  id: string;
  name: string;
  slug: string;
  subscription_tier: SubscriptionTier;
  subscription_status: SubscriptionStatus;
  created_at: string;
  data_sources: DataSource[];
  service_principal?: ServicePrincipal;
  usage_summary?: WorkspaceUsageSummary;
}

type TabId = 'overview' | 'users' | 'data-sources' | 'usage' | 'config';

const TABS: { id: TabId; label: string; icon: typeof Building2 }[] = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'data-sources', label: 'Data Sources', icon: Database },
  { id: 'usage', label: 'Usage Analytics', icon: BarChart3 },
  { id: 'config', label: 'Agent Config', icon: Settings },
];

const TIERS: SubscriptionTier[] = ['freemium', 'basic', 'standard', 'enterprise', 'enterprise_plus'];
const STATUSES: SubscriptionStatus[] = ['active', 'trial', 'suspended', 'cancelled'];

// Helper function to format billing period dates
function formatBillingPeriod(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const endDay = end.getDate();
  const year = end.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} — ${endDay}, ${year}`;
  } else {
    return `${startMonth} ${startDay} — ${endMonth} ${endDay}, ${year}`;
  }
}

export function WorkspaceDetail() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState<WorkspaceDetailData | null>(null);
  const [stats, setStats] = useState<WorkspaceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Data source modal state
  const [showDataSourceModal, setShowDataSourceModal] = useState(false);
  const [editingDataSource, setEditingDataSource] = useState<DataSource | undefined>(undefined);
  const [deletingDataSourceId, setDeletingDataSourceId] = useState<string | null>(null);
  const [syncingDataSourceId, setSyncingDataSourceId] = useState<string | null>(null);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editTier, setEditTier] = useState<SubscriptionTier>('freemium');
  const [editStatus, setEditStatus] = useState<SubscriptionStatus>('active');

  const activeTab = (searchParams.get('tab') as TabId) || 'overview';

  useEffect(() => {
    async function loadWorkspace() {
      if (!workspaceId) return;

      try {
        setLoading(true);
        const [workspaceData, statsData] = await Promise.all([
          workspaceApi.get(workspaceId),
          workspaceApi.getStats(workspaceId),
        ]);
        setWorkspace(workspaceData as WorkspaceDetailData);
        setStats(statsData);

        // Initialize edit form
        const ws = workspaceData as WorkspaceDetailData;
        setEditName(ws.name);
        setEditTier(ws.subscription_tier);
        setEditStatus(ws.subscription_status);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load workspace');
      } finally {
        setLoading(false);
      }
    }
    loadWorkspace();
  }, [workspaceId]);

  const handleTabChange = (tabId: TabId) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tabId);
    setSearchParams(params);
  };

  const handleSave = async () => {
    if (!workspaceId || !workspace) return;

    try {
      setSaving(true);
      await workspaceApi.update(workspaceId, {
        name: editName !== workspace.name ? editName : undefined,
        subscription_tier: editTier !== workspace.subscription_tier ? editTier : undefined,
        subscription_status: editStatus !== workspace.subscription_status ? editStatus : undefined,
      });

      setWorkspace({
        ...workspace,
        name: editName,
        subscription_tier: editTier,
        subscription_status: editStatus,
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update workspace');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (workspace) {
      setEditName(workspace.name);
      setEditTier(workspace.subscription_tier);
      setEditStatus(workspace.subscription_status);
    }
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!workspaceId) return;

    try {
      setDeleting(true);
      await workspaceApi.delete(workspaceId);
      navigate('/admin/workspaces');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete workspace');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDataSourceSaved = (dataSource: DataSource) => {
    if (!workspace) return;

    const existingIndex = workspace.data_sources.findIndex((ds) => ds.id === dataSource.id);
    if (existingIndex >= 0) {
      // Update existing
      const updatedDataSources = [...workspace.data_sources];
      updatedDataSources[existingIndex] = dataSource;
      setWorkspace({ ...workspace, data_sources: updatedDataSources });
    } else {
      // Add new
      setWorkspace({ ...workspace, data_sources: [...workspace.data_sources, dataSource] });
    }
  };

  const handleDeleteDataSource = async (dataSourceId: string) => {
    if (!workspaceId || !workspace) return;

    try {
      setDeletingDataSourceId(dataSourceId);
      await dataSourceApi.remove(workspaceId, dataSourceId);
      setWorkspace({
        ...workspace,
        data_sources: workspace.data_sources.filter((ds) => ds.id !== dataSourceId),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete data source');
    } finally {
      setDeletingDataSourceId(null);
    }
  };

  const handleSyncSchema = async (dataSourceId: string) => {
    if (!workspaceId) return;

    try {
      setSyncingDataSourceId(dataSourceId);
      setError(null);
      const result = await dataSourceApi.syncSchema(workspaceId, dataSourceId);
      // Show success feedback (could use toast, but for now we'll rely on the UI state change)
      console.log('Schema sync completed:', result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync schema');
    } finally {
      setSyncingDataSourceId(null);
    }
  };

  const openEditDataSource = (dataSource: DataSource) => {
    setEditingDataSource(dataSource);
    setShowDataSourceModal(true);
  };

  const openAddDataSource = () => {
    setEditingDataSource(undefined);
    setShowDataSourceModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !workspace) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/workspaces"
          className="inline-flex items-center gap-2 text-sm text-surface-600 hover:text-surface-800"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Workspaces
        </Link>
        <div className="bg-error-light border border-error/30 rounded-sm p-4 text-error">
          {error || 'Workspace not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-6">
      {/* Back Link */}
      <Link
        to="/admin/workspaces"
        className="inline-flex items-center gap-2 text-sm text-surface-600 hover:text-surface-800"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Workspaces
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-sm bg-primary-50 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-surface-800">
              {workspace.name}
            </h1>
            <p className="text-sm text-surface-500">{workspace.slug}</p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Card variant="surface" className="border-error/30 bg-error-light">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-sm bg-error/20">
              <AlertTriangle className="w-5 h-5 text-error" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-surface-800">Delete Workspace</h3>
              <p className="text-sm text-surface-600 mt-1">
                Are you sure you want to delete &quot;{workspace.name}&quot;? This action cannot be undone.
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  isLoading={deleting}
                >
                  Delete Workspace
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-200 pb-2">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors cursor-pointer ${
              activeTab === tab.id
                ? 'bg-primary-50 text-primary-600'
                : 'text-surface-600 hover:text-surface-800 hover:bg-surface-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Edit Form */}
          {editing && (
            <Card variant="surface">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-surface-800">Edit Workspace</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-1.5">
                      Subscription Tier
                    </label>
                    <select
                      value={editTier}
                      onChange={(e) => setEditTier(e.target.value as SubscriptionTier)}
                      className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                    >
                      {TIERS.map((tier) => (
                        <option key={tier} value={tier}>
                          {tier.replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-1.5">
                      Status
                    </label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value as SubscriptionStatus)}
                      className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                    >
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={handleCancel}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button isLoading={saving} onClick={handleSave}>
                    <Save className="w-4 h-4 mr-1" />
                    Save Changes
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Users"
                value={stats.total_users}
                icon={<Users className="w-5 h-5" />}
                color="indigo"
              />
              <StatCard
                title="Data Sources"
                value={stats.total_data_sources}
                icon={<Database className="w-5 h-5" />}
                color="teal"
              />
              <StatCard
                title="Investigations"
                value={stats.total_investigations}
                icon={<Search className="w-5 h-5" />}
                color="warning"
              />
              <StatCard
                title="Reports"
                value={stats.total_reports}
                icon={<FileText className="w-5 h-5" />}
                color="coral"
              />
            </div>
          )}

          {/* Usage Section */}
          {workspace.usage_summary && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-surface-800">Usage vs Limits</h3>
              </div>

              {/* Info bar */}
              <Card variant="surface" className="bg-primary-50/50">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-xs text-surface-500">Billing Period</p>
                      <p className="text-sm font-medium text-surface-800">
                        {formatBillingPeriod(
                          workspace.usage_summary.billing_period_start,
                          workspace.usage_summary.billing_period_end
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-surface-500">Days Remaining</p>
                      <p className="text-sm font-medium text-surface-800">
                        {workspace.usage_summary.days_until_reset} days
                      </p>
                    </div>
                  </div>
                  <TierBadge tier={workspace.subscription_tier} />
                </div>
              </Card>

              {/* Usage cards grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <UsageGaugeCard
                  label="Messages this period"
                  used={workspace.usage_summary.usage.messages.used}
                  limit={workspace.usage_summary.usage.messages.limit}
                />
                <UsageGaugeCard
                  label="Active Investigations"
                  used={workspace.usage_summary.usage.active_investigations.used}
                  limit={workspace.usage_summary.usage.active_investigations.limit}
                />
                <UsageGaugeCard
                  label="Data Sources"
                  used={workspace.usage_summary.usage.data_sources.used}
                  limit={workspace.usage_summary.usage.data_sources.limit}
                />
                <UsageGaugeCard
                  label="Reports Generated"
                  used={workspace.usage_summary.usage.reports_generated.used}
                  limit={workspace.usage_summary.usage.reports_generated.limit}
                />
              </div>
            </div>
          )}

          {/* Workspace Info */}
          <Card variant="surface">
            <h3 className="text-sm font-medium text-surface-800 mb-4">
              Workspace Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-surface-500">ID</p>
                <p className="text-surface-800 font-mono">{workspace.id}</p>
              </div>
              <div>
                <p className="text-surface-500">Slug</p>
                <p className="text-surface-800">{workspace.slug}</p>
              </div>
              <div>
                <p className="text-surface-500">Subscription Tier</p>
                <TierBadge tier={workspace.subscription_tier} />
              </div>
              <div>
                <p className="text-surface-500">Status</p>
                <StatusBadge status={workspace.subscription_status} />
              </div>
              <div>
                <p className="text-surface-500">Created</p>
                <p className="text-surface-800">
                  {new Date(workspace.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Card>

          {/* Service Principal Status */}
          <ServicePrincipalCard servicePrincipal={workspace.service_principal} />

        </div>
      )}

      {/* Data Sources Tab */}
      {activeTab === 'data-sources' && workspaceId && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-surface-800">Data Sources</h3>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={openAddDataSource}
              >
                Add Data Source
              </Button>
            </div>
          </div>

          {workspace.data_sources.length === 0 ? (
            <Card variant="glass" className="p-8 text-center">
              <Database className="w-12 h-12 text-surface-500 mx-auto mb-4" />
              <p className="text-surface-600">No data sources configured</p>
              <p className="text-sm text-surface-500 mt-1">
                Add a data source to start analyzing data
              </p>
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={openAddDataSource}
                >
                  Add Data Source
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {workspace.data_sources.map((ds) => {
                return (
                  <Card key={ds.id} variant="surface">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-success-light">
                          <Database className="w-5 h-5 text-success" />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-surface-800">
                            {ds.name}
                          </h4>
                          <p className="text-xs text-surface-500">
                            {ds.type} · {ds.host}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <DataSourceStatusBadge status={ds.status} />
                        {/* Only show sync button for data sources with catalogs (Databricks/Delta Sharing) */}
                        {ds.catalog && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSyncSchema(ds.id)}
                            isLoading={syncingDataSourceId === ds.id}
                            title="Sync schema and regenerate prompts"
                          >
                            <RefreshCw className={`w-4 h-4 ${syncingDataSourceId === ds.id ? 'animate-spin' : ''}`} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDataSource(ds)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDataSource(ds.id)}
                          isLoading={deletingDataSourceId === ds.id}
                          className="text-error hover:text-error"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && workspaceId && <WorkspaceUsersTab workspaceId={workspaceId} />}

      {activeTab === 'usage' && workspaceId && (
        <div className="space-y-8">
          <UsageAnalytics workspaceId={workspaceId} />
        </div>
      )}

      {activeTab === 'config' && workspaceId && <AgentConfigPanel workspaceId={workspaceId} />}

      {/* Data Source Modal */}
      {workspaceId && (
        <DataSourceModal
          isOpen={showDataSourceModal}
          onClose={() => {
            setShowDataSourceModal(false);
            setEditingDataSource(undefined);
          }}
          workspaceId={workspaceId}
          dataSource={editingDataSource}
          onSaved={handleDataSourceSaved}
        />
      )}

      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'indigo' | 'teal' | 'warning' | 'coral';
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  const colorClasses = {
    indigo: 'bg-primary-50 text-primary-600',
    teal: 'bg-success-light text-success',
    warning: 'bg-warning-light text-warning',
    coral: 'bg-error-light text-error',
  };

  return (
    <Card variant="surface">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-sm ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-xs text-surface-500">{title}</p>
          <p className="text-xl font-semibold text-surface-800">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function ServicePrincipalCard({ servicePrincipal }: { servicePrincipal?: ServicePrincipal }) {
  if (!servicePrincipal) {
    return (
      <Card variant="surface">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-sm bg-surface-100">
            <Key className="w-5 h-5 text-surface-500" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-surface-800">
              Databricks Service Principal
            </h3>
            <p className="text-xs text-surface-500">
              Not configured for this workspace
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const statusConfig: Record<
    string,
    { icon: typeof CheckCircle; color: string; bgColor: string; label: string }
  > = {
    active: {
      icon: CheckCircle,
      color: 'text-success',
      bgColor: 'bg-success-light',
      label: 'Active',
    },
    provisioning: {
      icon: Clock,
      color: 'text-warning',
      bgColor: 'bg-warning-light',
      label: 'Provisioning',
    },
    failed: {
      icon: XCircle,
      color: 'text-error',
      bgColor: 'bg-error-light',
      label: 'Failed',
    },
    disabled: {
      icon: XCircle,
      color: 'text-surface-500',
      bgColor: 'bg-surface-100',
      label: 'Disabled',
    },
  };

  const config = statusConfig[servicePrincipal.status] || statusConfig.disabled;
  const StatusIcon = config.icon;

  // Calculate days until rotation
  let rotationInfo = null;
  if (servicePrincipal.rotation_due_at) {
    const dueDate = new Date(servicePrincipal.rotation_due_at);
    const now = new Date();
    const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil <= 14) {
      rotationInfo = (
        <span className="text-warning">
          Rotation due in {daysUntil} days
        </span>
      );
    }
  }

  return (
    <Card variant="surface">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-sm ${config.bgColor}`}>
            <Key className={`w-5 h-5 ${config.color}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-surface-800">
                Databricks Service Principal
              </h3>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-sm ${config.bgColor} ${config.color}`}
              >
                <StatusIcon className="w-3 h-3" />
                {config.label}
              </span>
            </div>
            {servicePrincipal.name && (
              <p className="text-xs text-surface-500 mt-1">
                {servicePrincipal.name}
              </p>
            )}
          </div>
        </div>
      </div>

      {servicePrincipal.status === 'failed' && servicePrincipal.error_message && (
        <div className="mt-4 p-3 rounded-sm bg-error-light border border-error/20">
          <p className="text-xs text-error">
            <strong>Error:</strong> {servicePrincipal.error_message}
          </p>
          {servicePrincipal.error_at && (
            <p className="text-xs text-surface-500 mt-1">
              Failed at: {new Date(servicePrincipal.error_at).toLocaleString()}
            </p>
          )}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        {servicePrincipal.id && (
          <div>
            <p className="text-surface-500">SP ID</p>
            <p className="text-surface-800 font-mono truncate" title={servicePrincipal.id}>
              {servicePrincipal.id}
            </p>
          </div>
        )}
        {servicePrincipal.application_id && (
          <div>
            <p className="text-surface-500">Application ID</p>
            <p className="text-surface-800 font-mono truncate" title={servicePrincipal.application_id}>
              {servicePrincipal.application_id.slice(0, 8)}...
            </p>
          </div>
        )}
        {servicePrincipal.last_rotated_at && (
          <div>
            <p className="text-surface-500">Last Rotated</p>
            <p className="text-surface-800">
              {new Date(servicePrincipal.last_rotated_at).toLocaleDateString()}
            </p>
          </div>
        )}
        {servicePrincipal.rotation_due_at && (
          <div>
            <p className="text-surface-500">Next Rotation</p>
            <p className="text-surface-800">
              {rotationInfo || new Date(servicePrincipal.rotation_due_at).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>

      {servicePrincipal.credentials_secret_arn && (
        <div className="mt-4 pt-4 border-t border-surface-200">
          <p className="text-xs text-surface-500">Credentials ARN</p>
          <p className="text-xs text-surface-600 font-mono truncate" title={servicePrincipal.credentials_secret_arn}>
            {servicePrincipal.credentials_secret_arn}
          </p>
        </div>
      )}
    </Card>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const colors: Record<string, string> = {
    freemium: 'bg-surface-100 text-surface-500',
    basic: 'bg-primary-50 text-primary-600',
    standard: 'bg-success-light text-success',
    enterprise: 'bg-warning-light text-warning',
    enterprise_plus: 'bg-error-light text-error',
  };

  return (
    <span className={`inline-block mt-1 px-2.5 py-1 text-xs font-medium rounded-sm ${colors[tier] || colors.freemium}`}>
      {tier.replace('_', ' ')}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-success-light text-success',
    trial: 'bg-warning-light text-warning',
    suspended: 'bg-error-light text-error',
    cancelled: 'bg-surface-100 text-surface-500',
  };

  return (
    <span className={`inline-block mt-1 px-2.5 py-1 text-xs font-medium rounded-sm ${colors[status] || colors.active}`}>
      {status}
    </span>
  );
}

function DataSourceStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    connected: 'bg-success-light text-success',
    error: 'bg-error-light text-error',
    disabled: 'bg-surface-100 text-surface-500',
  };

  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-sm ${colors[status] || colors.disabled}`}>
      {status}
    </span>
  );
}

export default WorkspaceDetail;
